/**
 * FeedbackPage — `/customer-service/feedback`.
 *
 * Live tiles + inbox. Backed by the new aggregates surfaces on
 * service-service-desk:
 *   - GET /v1/service-desk/feedback/aggregate  — CSAT / NPS / counts
 *   - GET /v1/service-desk/feedback            — inbox list
 *   - POST /v1/service-desk/feedback/:id/resolve — acknowledge a row
 */
import { type JSX, useState } from 'react';
import { Star } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getFeedbackAggregate,
  listFeedback,
  resolveFeedback,
  type FeedbackItem,
} from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

type ResolvedFilter = 'all' | 'open' | 'resolved';

export function FeedbackPage(): JSX.Element {
  const qc = useQueryClient();
  const [resolvedFilter, setResolvedFilter] = useState<ResolvedFilter>('all');

  const aggregate = useQuery({
    queryKey: ['cs', 'feedback', 'aggregate'],
    queryFn: () => getFeedbackAggregate(),
    retry: false,
    staleTime: 30_000,
  });

  const inboxQuery = useQuery({
    queryKey: ['cs', 'feedback', 'list', { resolvedFilter }],
    queryFn: () =>
      listFeedback({
        pageSize: 50,
        ...(resolvedFilter === 'open' ? { resolved: false } : {}),
        ...(resolvedFilter === 'resolved' ? { resolved: true } : {}),
      }),
    retry: false,
    staleTime: 15_000,
  });

  const resolveMut = useMutation({
    mutationFn: (id: string) => resolveFeedback(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cs', 'feedback'] });
    },
  });

  const aggDenied =
    aggregate.isError && aggregate.error instanceof ApiError && (aggregate.error.status === 401 || aggregate.error.status === 403);
  const inboxDenied =
    inboxQuery.isError && inboxQuery.error instanceof ApiError && (inboxQuery.error.status === 401 || inboxQuery.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader title="Customer Feedback" description="CSAT, NPS and inbox aggregated from service desk feedback." />

      <div data-testid="feedback-kpis" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="CSAT"
          value={aggregate.isPending ? '—' : aggDenied ? '—' : `${aggregate.data?.csat ?? 0}%`}
          hint={aggDenied ? 'Permission required' : `${aggregate.data?.totalCount ?? 0} responses`}
          icon={Star}
          tone={aggDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="NPS"
          value={aggregate.isPending ? '—' : aggDenied ? '—' : String(aggregate.data?.nps ?? 0)}
          hint={aggDenied ? 'Permission required' : 'promoters - detractors / total'}
          tone={aggDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Open feedback"
          value={aggregate.isPending ? '—' : aggDenied ? '—' : String(aggregate.data?.openInbox ?? 0)}
          hint={aggDenied ? 'Permission required' : 'unresolved inbox rows'}
          tone={aggDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Resolved this week"
          value={aggregate.isPending ? '—' : aggDenied ? '—' : String(aggregate.data?.resolvedThisWeek ?? 0)}
          hint={aggDenied ? 'Permission required' : 'last 7 days'}
          tone={aggDenied ? 'warning' : 'default'}
        />
      </div>

      <section className="bg-white border border-border rounded-xl">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-sm font-semibold text-gray-900">Inbox</h2>
          <label className="text-xs text-muted">
            <span className="mr-2">Filter</span>
            <select
              data-testid="feedback-filter"
              aria-label="Resolved filter"
              value={resolvedFilter}
              onChange={(e) => setResolvedFilter(e.target.value as ResolvedFilter)}
              className="px-2 py-1 border border-border rounded"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
        </header>

        {inboxQuery.isPending ? (
          <p className="p-8 text-center text-sm text-muted">Loading…</p>
        ) : inboxDenied ? (
          <p className="p-8 text-center text-sm text-muted">Permission required (ServiceDesk/readFeedback).</p>
        ) : inboxQuery.isError ? (
          <p className="p-8 text-center text-sm text-danger">{(inboxQuery.error as Error).message}</p>
        ) : (inboxQuery.data?.items.length ?? 0) === 0 ? (
          <p className="p-8 text-center text-sm text-muted">No feedback yet.</p>
        ) : (
          <table data-testid="feedback-table" className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Case</th>
                <th className="text-left px-4 py-2">Rating</th>
                <th className="text-left px-4 py-2">Comment</th>
                <th className="text-left px-4 py-2">Submitted</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(inboxQuery.data?.items ?? []).map((row: FeedbackItem) => (
                <tr key={row.id} data-testid={`feedback-row-${row.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{row.caseNumber}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">
                    <span data-testid={`feedback-rating-${row.id}`}>{row.rating}/5</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-muted max-w-md truncate">{row.comment ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(row.submittedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {row.resolved ? (
                      <span className="text-success">Resolved</span>
                    ) : (
                      <span className="text-warning">Open</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {row.resolved ? null : (
                      <button
                        data-testid={`feedback-resolve-${row.id}`}
                        onClick={() => resolveMut.mutate(row.id)}
                        disabled={resolveMut.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                      >
                        Acknowledge
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
